// ============================================================
// ATTACK CALC - Pure functions for pre-hit attack calculations.
// Extracted from useCombat handleAttack.
// ============================================================
import { calculateHits } from './constants';
import { hasWS, getWSP } from './weaponSpecials';
import {
  RANGE_LABELS,
  clampSkill,
  sumRows,
  sizeRows,
  rangeModFor,
  rangeRows,
  weaponRows,
  meleeFuryRows,
  toggleRows,
  calledShotRows,
} from './skillTerms';
import type { ShotTerms } from './skillTerms';
import type { Slot } from '../types';
import type { ModifierDetail } from './structuredNarrative';
import { crowdExtraHits } from './bodyShape';

export { RANGE_MODS, RANGE_LABELS } from './skillTerms';

export const RANGE_BAND_LABELS: Record<string, string> = {
  pointBlank: 'Point Blank',
  short: 'Short Range',
  normal: 'Normal Range',
  long: 'Long Range',
  extreme: 'Extreme Range',
};

/**
 * Calculate the effective (modified) skill value for an attack roll,
 * incorporating size, range, weapon specials (Defensive, TwinLinked).
 * The terms are the rows `calcSkillBreakdown` prints (see skillTerms.ts),
 * summed and clamped to 1..100.
 */
export function calcModifiedSkill(
  slot: Slot,
  attackMod: number,
  targetSize: string,
  rangeBand: string,
  isMelee: boolean,
  hatred: boolean = false,
  frenzied: boolean = false,
  removeLongRangePenalty: boolean = false,
  removeExtremeRangePenalty: boolean = false,
  aimed: boolean = false,
): number {
  const sp = slot.specialRules;
  const shot: ShotTerms = { sp, rangeBand, isMelee, aimed, removeLongRangePenalty, removeExtremeRangePenalty };
  return clampSkill(
    slot.skillValue +
      attackMod +
      sumRows(sizeRows(sp, targetSize)) +
      sumRows(weaponRows(slot, shot)) +
      rangeModFor(shot) +
      sumRows(meleeFuryRows(sp, isMelee, hatred, frenzied)),
  );
}

/**
 * Determine the roll threshold at which a ranged weapon jams.
 * Melee weapons never jam (returns 101).
 *
 * Official Reliable (v8.197.17): jams only on a natural 00 (roll 100),
 * and even that is confirmed by a second d100 in the attack flow - 96+
 * upholds the jam, anything else is just a miss. The old sim behaviour
 * (never jams at all) survives as the `reliableNeverJams` house rule.
 */
/**
 * The metres inside which this weapon refuses to fire: Standoff's own
 * minimum, Indirect's arc floor, or both - the larger wins (v8.280.0).
 * 0 means no minimum. Without a battlefield distance the gate is inert.
 */
export function minimumRangeOf(specialRules: string[]): number {
  return Math.max(getWSP(specialRules, 'Standoff'), getWSP(specialRules, 'Indirect'));
}

/**
 * Suppressing Fire's widened jam band (v8.307.0). RAW: a suppression
 * burst jams on 94+, where an ordinary weapon jams on 96+.
 */
const SUPPRESSION_JAM_THRESHOLD = 94;

/** The arc a suppression burst sprays across, in degrees (v8.307.0). */
export const SUPPRESSION_CONE_DEGREES = 45;

export function calcJamThreshold(
  specialRules: string[],
  isMelee: boolean,
  reliableNeverJams: boolean = false,
  opts: { suppressing?: boolean; jamFloor?: number } = {},
): number {
  // Blessed (v8.282.0): the machine spirit never falters - per-weapon,
  // where reliableNeverJams is the table-wide house rule.
  if (isMelee || hasWS(specialRules, 'Blessed')) return 101;
  // Temperamental(X) (v8.282.0): a custom jam floor - the lower wins,
  // so the drawback can never accidentally IMPROVE a weapon.
  const temperamental = getWSP(specialRules, 'Temperamental');
  const base = baseJamThreshold(specialRules, reliableNeverJams);
  const floored = temperamental > 0 ? Math.min(base, temperamental) : base;
  // Suppressing Fire tightens the band, and TIGHTENS it only: a weapon
  // that never jams at all (Blessed, or Reliable under the house rule -
  // both 101) keeps that, and an Unreliable 91 is already worse. A `jam`
  // exposure on the target (v8.368.1) tightens the band the same way.
  const suppressed = tightenJam(floored, opts.suppressing ? SUPPRESSION_JAM_THRESHOLD : 0);
  return tightenJam(suppressed, opts.jamFloor || 0);
}

function baseJamThreshold(specialRules: string[], reliableNeverJams: boolean): number {
  if (hasWS(specialRules, 'Unreliable')) return 91;
  if (hasWS(specialRules, 'Reliable')) return reliableNeverJams ? 101 : 100;
  return 96;
}

/** Lower a jam band to `floor` - never raise it, and never touch a weapon that cannot jam (101). */
function tightenJam(base: number, floor: number): number {
  return floor > 0 && base <= 100 ? Math.min(base, floor) : base;
}

const MELEE_MODES: Record<string, string> = {
  swift: 'swift',
  semi: 'swift',
  lightning: 'lightning',
  full: 'lightning',
};

/**
 * The mode a swing's hits are counted in and the rate of fire that caps
 * them. A blade's Swift and Lightning Attack are capped by nothing the
 * slot carries (its rateOfFire is a gun's); a bonus attack is one hit.
 * The ONE rule for the roll (hooks/attack/shot) and both forecasts
 * (probability, defenceProbability) - they drifted apart once (v8.390.2).
 */
export function hitMode(
  fireMode: string,
  slot: Pick<Slot, 'rateOfFire'>,
  isMelee: boolean,
  isBonusAttack: boolean,
): { effectiveMode: string; rof: number } {
  // Phase 7: Bonus attack (Furious Assault) is always single mode
  if (isBonusAttack) return { effectiveMode: 'single', rof: 1 };
  if (isMelee) {
    const mode = MELEE_MODES[fireMode];
    return mode ? { effectiveMode: mode, rof: 99 } : { effectiveMode: fireMode, rof: 1 };
  }
  if (fireMode === 'semi') return { effectiveMode: fireMode, rof: slot.rateOfFire.semi };
  if (fireMode === 'full') return { effectiveMode: fireMode, rof: slot.rateOfFire.full };
  return { effectiveMode: fireMode, rof: 1 };
}

/**
 * Calculate total number of hits from DoS, fire mode, RoF, and weapon specials
 * (Blast, Scatter, TwinLinked, Storm).
 *
 * `rangeBand` arrived with official Scatter (v8.197.11) and defaults to
 * 'normal' so melee callers and old fixtures stay valid.
 */
export function calcTotalHits(
  dos: number,
  effectiveMode: string,
  rofValue: number,
  specialRules: string[],
  rangeBand: string = 'normal',
  opts: HitOpts = {},
): { hits: number; notes: string[] } {
  const notes: string[] = [];
  const ctx: HitRiderCtx = { dos, specialRules, rangeBand, opts, notes };
  const hits = HIT_RIDERS.reduce((n, rider) => rider(n, ctx), baseHitCount(ctx, effectiveMode, rofValue));
  return { hits, notes };
}

interface HitOpts {
  /** The TARGET is a horde, and `isMelee` the swing is melee: Reaping(X) (v8.288.0). */
  isHorde?: boolean;
  isMelee?: boolean;
  suppressing?: boolean;
  /** The target's `hits` exposure (v8.368.1). */
  shift?: number;
  /** The ATTACKER's magnitude when it is a horde (bodyShape `attackingMagnitude`; 0 otherwise): +1 hit per 10. */
  attackerMagnitude?: number;
}

/** What each hit rider reads; a rider that fires appends its note. */
interface HitRiderCtx {
  dos: number;
  specialRules: string[];
  rangeBand: string;
  opts: HitOpts;
  notes: string[];
}

type HitRider = (hits: number, ctx: HitRiderCtx) => number;

const plural = (n: number) => (n > 1 ? 's' : '');

/**
 * Suppressing Fire REPLACES the fire-mode formula (v8.307.0): the burst
 * is sprayed to make people flinch, not aimed, so RAW gives one hit plus
 * one per two full Degrees of Success however fat the RoF. The weapon's
 * own riders still apply - they are properties of the gun, not of the
 * aiming.
 */
function baseHitCount(ctx: HitRiderCtx, effectiveMode: string, rofValue: number): number {
  if (!ctx.opts.suppressing) return calculateHits(ctx.dos, effectiveMode, rofValue);
  const hits = 1 + Math.floor(ctx.dos / 2);
  ctx.notes.push('Suppressing Fire: 1 hit +1 per 2 DoS = ' + hits);
  return hits;
}

/** Reaping(X) (v8.288.0): the sweeping blade - +X hits when a melee attack lands among a crowd. */
const reapingRider: HitRider = (hits, { specialRules, opts, notes }) => {
  const reaping = opts.isMelee && opts.isHorde ? getWSP(specialRules, 'Reaping') : 0;
  if (reaping <= 0) return hits;
  notes.push('Reaping: +' + reaping + ' hit' + plural(reaping) + ' into the horde');
  return hits + reaping;
};

/**
 * Official DW Scatter: at Point Blank the spread bites - one extra hit
 * per two full DoS (v8.197.11; replaced the ±3 damage swings).
 */
const scatterRider: HitRider = (hits, { dos, specialRules, rangeBand, notes }) => {
  const spread = hasWS(specialRules, 'Scatter') && rangeBand === 'pointBlank' ? Math.floor(dos / 2) : 0;
  if (spread <= 0) return hits;
  notes.push('Scatter: +' + spread + ' hit' + plural(spread) + ' at Point Blank');
  return hits + spread;
};

/** Official: the second barrel scores its hit on 2+ DoS, not on every success (v8.197.10). */
const twinLinkedRider: HitRider = (hits, { dos, specialRules, notes }) => {
  if (!(hasWS(specialRules, 'TwinLinked') && dos >= 2)) return hits;
  notes.push('Twin-Linked: +1 hit (2+ DoS)');
  return hits + 1;
};

const stormRider: HitRider = (hits, { specialRules, notes }) => {
  if (!hasWS(specialRules, 'Storm')) return hits;
  notes.push('Storm: hits doubled to ' + hits * 2);
  return hits * 2;
};

/**
 * A horde attacking (ruling 2026-10-09; docs/rules-spec.md, Hordes): +1 hit per full 10 magnitude it has left, on
 * each attack - one roll, one defence prompt, the extra hits joining the burst. The crowd's, not the weapon's: it
 * comes after Storm, so Storm doubles only the gun's hits.
 */
const crowdRider: HitRider = (hits, { opts, notes }) => {
  const magnitude = opts.attackerMagnitude ?? 0;
  const extra = crowdExtraHits(magnitude);
  if (extra <= 0 || !(hits > 0)) return hits;
  notes.push(`Horde (magnitude ${magnitude}): +${extra} hit${plural(extra)}`);
  return hits + extra;
};

/** The target's `hits` exposure (v8.368.1), last - an attack that landed still lands at least once. */
const exposureRider: HitRider = (hits, { opts, notes }) => {
  const shift = opts.shift;
  if (!shift || !(hits > 0)) return hits;
  const shifted = Math.max(1, hits + shift);
  if (shifted !== hits) notes.push(exposureNote(shift, shifted));
  return shifted;
};

function exposureNote(shift: number, shifted: number): string {
  const sign = shift > 0 ? '+' : '';
  const unit = Math.abs(shift) === 1 ? 'hit' : 'hits';
  return `Exposed: ${sign}${shift} ${unit} → ${shifted}`;
}

/**
 * The riders, in order: every additive rider before Storm doubles the
 * count, the attacking horde's own hits after it, and the exposure last. AREA SATURATION (ruling 2026-08-31):
 * Blast no longer clamps the count to 1 - the burst's hits stand, and
 * the area path applies them to EVERY victim the template catches. The
 * old "Blast replaces multi-hit" ruling (and the v8.196.6 clamp-ordering
 * ruling it forced) is superseded; see the ADR.
 */
const HIT_RIDERS: readonly HitRider[] = [
  reapingRider,
  scatterRider,
  twinLinkedRider,
  stormRider,
  crowdRider,
  exposureRider,
];

/**
 * Calculate effective penetration after weapon special modifications
 * (Mono, RazorSharp). `_rangeBand` is unused - range no longer changes pen
 * here (Melta's doubling is gated in damageCalc) - and kept so the
 * callers' positional arguments stay valid.
 */
export function calcEffectivePen(
  basePen: number,
  specialRules: string[],
  dos: number,
  _rangeBand: string,
): { pen: number; notes: string[] } {
  let pen = basePen;
  const notes: string[] = [];
  if (hasWS(specialRules, 'Mono')) pen += 2;
  if (hasWS(specialRules, 'RazorSharp') && dos >= 2) {
    pen *= 2;
    notes.push('Razor Sharp: Pen doubled to ' + pen);
  }
  return { pen, notes };
}

/**
 * What the log should say about Melta, given the band actually rolled.
 *
 * Extracted so it can be tested. It used to read "Melta: double Pen at
 * Short Range (GM adjusts)" whenever the weapon had Melta - wording from
 * before the app knew the range band - and it sits in the same list as
 * notes describing what the attack DID, so it was reported as Melta
 * firing at Normal range. It never fired: the doubling is gated in
 * `damageCalc`. The log was simply saying something untrue.
 */
export function meltaNote(pen: number, rangeBand: string): string {
  const label = RANGE_LABELS[rangeBand] || rangeBand;
  const doubled = rangeBand === 'pointBlank' || rangeBand === 'short';
  return doubled
    ? 'Melta: Pen doubled to ' + pen * 2 + ' (' + label + ')'
    : 'Melta: NOT doubled - ' + label + ' range, needs Short or closer';
}

/**
 * What the log should say about Scatter, given the band rolled.
 *
 * Both halves are now real: `calcModifiedSkill` applies +10 to hit at
 * Point Blank (v7.53.0) and `damageCalc` gives +3 there and −3 at Long or
 * Extreme. Until v7.53.0 the note claimed the to-hit bonus and nothing
 * implemented it - the Melta defect, and a worse one, since a to-hit
 * bonus decides whether the shot lands at all.
 */
export function scatterNote(rangeBand: string): string {
  const label = RANGE_LABELS[rangeBand] || rangeBand;
  if (rangeBand === 'pointBlank') return 'Scatter: +10 to hit, +1 hit per 2 DoS (Point Blank)';
  if (rangeBand === 'long' || rangeBand === 'extreme') return `Scatter: worn armour counts double (${label})`;
  return `Scatter: no range effect at ${label}`;
}

/**
 * Maximal, as applied: +1d10 damage ON THE FIRST HIT ONLY, +2 Pen, triple
 * ammo. The first-hit qualifier was missing and matters on a burst.
 */
export function maximalNote(): string {
  return 'Maximal: +1d10 damage on the first hit, +2 Pen, +2 Blast radius, 3× ammo';
}

/**
 * The band an attack rolls at: the map's, unless a person chose one.
 *
 * 'auto' is the default. It falls back to Normal when the map cannot
 * answer - nobody placed, no weapon range, beyond Extreme - which is what
 * the control defaulted to before it could ask, so a session with no
 * battlefield behaves exactly as it always did.
 */
export function resolveRangeBand(choice: string, fromMap: string | null | undefined): string {
  if (choice && choice !== 'auto') return choice;
  return fromMap || 'normal';
}

export interface SkillBreakdownParams {
  slot: Slot;
  attackMod: number;
  targetSize: string;
  rangeBand: string;
  isMelee: boolean;
  hatred?: boolean;
  frenzied?: boolean;
  removeLongRangePenalty?: boolean;
  removeExtremeRangePenalty?: boolean;
  // Combat action toggles
  isAimHalf?: boolean;
  isAimFull?: boolean;
  isAllOutAttack?: boolean;
  isGuardedAttack?: boolean;
  isCharge?: boolean;
  isGangUp?: boolean;
  hasBerserkCharge?: boolean;
  // Called shot
  isCalledShot?: boolean;
  calledShotReduction?: number;
  /** The target's own share of that reduction - a `called` exposure (v8.368.2), named on its own row. */
  calledShotExposure?: number;
  /** The penalty is waived by the Controls switch; its row says so in place of the talent's. */
  calledShotIgnored?: boolean;
  // Custom modifier passed through attackMod - we split it out for labeling
  customModifiers?: { label: string; value: number }[];
}

/**
 * Build a structured modifier breakdown for the attack skill calculation.
 * Mirrors the logic of calcModifiedSkill + useAttackExecution toggle bonuses,
 * producing labeled entries that sum to the clamped modified skill.
 *
 * The "Attack Modifier" row is whatever is left in `attackMod` that no
 * row explained. It should normally be nothing at all: a line there means
 * the app applied a modifier it cannot name, which is worth seeing. The
 * explained part is the rows that CAME OUT OF `attackMod` (toggles,
 * called shot, custom). It used to be worked out by re-adding the toggles
 * by hand, a second copy of `computeToggleModifier` that had already
 * drifted from the first: it knew nothing about Aim, so every aimed shot
 * reported a mysterious "Attack Modifier" of +10 or +20. Summing the rows
 * the reader is shown cannot drift.
 */
export function calcSkillBreakdown(params: SkillBreakdownParams): ModifierDetail[] {
  const { slot, attackMod, isMelee, hatred = false, frenzied = false } = params;
  const shot = shotTermsOf(params);
  const toggles = toggleRows({ ...toggleFlagsOf(params), sp: slot.specialRules, isMelee });
  const extra = [...calledRowsOf(params), ...customRows(params.customModifiers ?? [])];
  const mods: ModifierDetail[] = [
    { label: isMelee ? 'Base WS' : 'Base BS', value: slot.skillValue },
    ...sizeRows(slot.specialRules, params.targetSize),
    ...rangeRows(shot),
    ...weaponRows(slot, shot),
    ...toggles,
    ...meleeFuryRows(slot.specialRules, isMelee, hatred, frenzied),
    ...extra,
  ];
  const remainder = attackMod - sumRows([...toggles, ...extra]);
  if (remainder !== 0) mods.push({ label: 'Attack Modifier', value: remainder });
  const rawSum = sumRows(mods);
  const clamped = clampSkill(rawSum);
  if (clamped !== rawSum) mods.push({ label: 'Clamped', value: clamped - rawSum });
  return mods;
}

function shotTermsOf(params: SkillBreakdownParams): ShotTerms {
  const { removeLongRangePenalty = false, removeExtremeRangePenalty = false } = params;
  const aimed = !!params.isAimHalf || !!params.isAimFull;
  const { rangeBand, isMelee } = params;
  return { sp: params.slot.specialRules, rangeBand, isMelee, aimed, removeLongRangePenalty, removeExtremeRangePenalty };
}

/** The toggles, absent read as off. */
function toggleFlagsOf(params: SkillBreakdownParams) {
  const on = (flag: boolean | undefined) => flag ?? false;
  return {
    isAimHalf: on(params.isAimHalf),
    isAimFull: on(params.isAimFull),
    isAllOutAttack: on(params.isAllOutAttack),
    isGuardedAttack: on(params.isGuardedAttack),
    isCharge: on(params.isCharge),
    isGangUp: on(params.isGangUp),
    hasBerserkCharge: on(params.hasBerserkCharge),
  };
}

function calledRowsOf(params: SkillBreakdownParams): ModifierDetail[] {
  const { isCalledShot = false, calledShotReduction = 0, calledShotExposure = 0 } = params;
  if (!isCalledShot) return [];
  return calledShotRows(params.isMelee, calledShotReduction, calledShotExposure, params.calledShotIgnored);
}

/** Custom modifiers (from the CustomModifier component, folded into attackMod); a zero moved nothing. */
function customRows(customModifiers: { label: string; value: number }[]): ModifierDetail[] {
  return customModifiers
    .filter((cm) => cm.value !== 0)
    .map((cm) => ({ label: 'Custom: ' + cm.label, value: cm.value }));
}
